import { ElementType } from 'react';
import * as React from 'react';

import cx from 'classnames';

import { Box } from '~shared/components/Box';
import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { ReactNodeNoStrings } from '~shared/types';

export interface Props {
  children: React.ReactNode;
  itemCount?: number;
  icon: ReactNodeNoStrings;
  isSelected?: boolean;
  href?: string;
  onClick?: () => void;
  elementType?: ElementType;
}

export function SidebarItem({
  children,
  itemCount,
  icon,
  isSelected = false,
  href,
  onClick,
  elementType = 'a',
}: Props) {
  const itemProps = {
    href,
    onClick,
  };
  /**
   * Assigning the property value here because explicitly setting it
   * to `undefined` causes a TS error for some reason. 🤷‍♂️
   */
  if (isSelected) itemProps['aria-current'] = 'page';

  const itemClass = cx(
    'group relative flex items-start w-full px-6 py-4',
    'text-base font-500 font-sans leading-8 no-underline rounded-2 outline-none',
    elementType === 'button' && 'border-0 cursor-pointer',
    isSelected
      ? 'text-blue-900 bg-grey-50 hocus:bg-grey-100'
      : 'text-grey-900 bg-transparent hocus:bg-grey-50'
  );

  const ref = React.useRef<HTMLParagraphElement | null>(null);
  const [truncated, setTruncated] = React.useState<boolean>(false);
  React.useLayoutEffect(() => {
    const { current } = ref;
    if (!current) return;
    setTruncated(current.offsetWidth < current.scrollWidth);
  }, [ref]);
  const compactNumber = Intl.NumberFormat('en', { notation: 'compact' });
  return (
    <div className="py-1">
      <TooltipV2 label={children as string} isDisabled={!truncated}>
        <Box tagName={elementType} className={itemClass} {...itemProps}>
          <FocusIndicator />
          <div className="flex flex-none">{icon}</div>
          <p ref={ref} className="pl-4 pr-2 truncate w-full">
            {children}
          </p>
          {itemCount !== undefined && (
            <p className="text-xs font-600">
              {compactNumber.format(itemCount)}
            </p>
          )}
        </Box>
      </TooltipV2>
    </div>
  );
}
